import { isOnOrigin } from '@app/shared'
import { isAppHost, isPlatformHost } from '@app/shared/brand'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useTRPC } from '../lib/trpc'

/**
 * The exchange: one site's session, converted into another's.
 *
 * Our products are separate registrable domains, so a cookie set on one is
 * invisible to the others and there is nothing to share. What a person reaching
 * a product cold gets instead is one bounce through here: if they hold a
 * session at the platform's address, the API mints a one-minute token for the
 * site they were going to and this sends them back to redeem it. If they do
 * not, they go back the way they came carrying `sso=none`, which is that site's
 * cue to show its own sign-in screen rather than bounce again.
 *
 * **It answers on the platform's address and nowhere else.** One place converts
 * a session into another, so there is one thing to reason about instead of each
 * product deciding on behalf of the next.
 *
 * It renders nothing. There is nothing to say in the time this takes, and a
 * spinner between two redirects is a flash of chrome that reads as a fault.
 */
export function SsoBounce({
  to,
  next,
  have,
}: {
  to: string
  next: string
  /**
   * The site sending us here already has a session of its own.
   *
   * Then this is an errand rather than a sign-in — it came to tell us, and
   * handing a token back would redeem into a SECOND session at a site that
   * already has one. So it goes straight home.
   */
  have?: boolean
}) {
  const trpc = useTRPC()
  /*
   * Its own read of who is asking, rather than the shell's.
   *
   * This page is reached moments after a sign-in redeemed here, so the answer
   * in hand is the one from before it. What matters is whether the request in
   * flight has come back — `isFetching`, not `isLoading`, which is only ever
   * true the first time. Answering early reports no session and sends somebody
   * to a sign-in screen they did not need.
   */
  const me = useQuery(trpc.auth.me.queryOptions())
  const handoff = useMutation(trpc.auth.handoff.mutationOptions())
  /*
   * Once, whatever React does with this component. Every branch below leaves
   * the page, so a second run can only burn a second token or race the first
   * navigation.
   */
  const went = useRef(false)

  useEffect(() => {
    if (went.current || me.isFetching || me.isPending) return
    // A target that is not one of ours is refused by the API, and there is
    // nowhere safe to send somebody who arrived with one, so this stops.
    let target: URL
    try {
      target = new URL(to)
    } catch {
      return
    }
    // Only where a session can live. Every branch below sends the browser
    // there, including the two that ask the API nothing, so an address that is
    // not ours would make this page a way off our sites.
    if (!isAppHost(target.hostname) && !isPlatformHost(target.hostname)) return
    // And only to a path on it: `next` resolved against the target has to stay
    // on the target, since a protocol-relative path would leave it.
    if (!isOnOrigin(next, target.origin)) return
    went.current = true

    const back = (found: boolean) => {
      const url = new URL(next, target.origin)
      // Only a fruitless trip is marked, and it is marked so that site shows a
      // sign-in screen instead of asking again.
      if (!found) url.searchParams.set('sso', 'none')
      window.location.assign(url.toString())
    }
    if (have) return back(true)
    if (!me.data) return back(false)
    handoff.mutate(
      { to: target.origin, next },
      {
        onSuccess: (r) => {
          /*
           * Say where this came from. Arriving at a product, a session would
           * otherwise turn straight round to tell the platform about itself —
           * which the platform already knows, and the trip costs the address
           * the person was going to.
           */
          const url = new URL(r.url)
          url.searchParams.set('via', 'platform')
          window.location.assign(url.toString())
        },
        // A refused handoff is the same outcome as no session: the person
        // arrives where they were going and signs in there.
        onError: () => back(false),
      },
    )
  }, [me.data, me.isFetching, me.isPending, to, next, have, handoff])

  return null
}
